import React, { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { FiSearch, FiChevronDown } from 'react-icons/fi';
import { usePersonas } from '../../hooks/usePersonas';
import { useChatSessions } from '../../hooks/useChatSessions';
import { useRecentChatPersonas } from '../../hooks/useRecentChatPersonas';
import { useTopTags } from '../../hooks/useTopTags';
import { useFilteredList } from '../../hooks/useFilteredList';
import { useListboxKeyboard } from '../../hooks/useListboxKeyboard';
import { getPersonaQualityTier } from '../../utils/persona_quality';
import { PersonaPickerRow } from './PersonaPickerRow';
import type { Persona } from '../../types/persona';

interface NewChatPickerPanelProps {
  onClose: () => void;
  onSelect: (persona: Persona) => void;
  initialSelectedTags?: string[];
  listboxId: string;
  titleId: string;
  showRecents?: boolean;    // default true — chat-launch shows recents; embedded select hides them
  showFooterNav?: boolean;  // default true — chat-launch shows browse/forge nav; embedded hides it
  maxHeight?: number;       // viewport-clamped panel height (px) from NewChatPicker; falls back to 600
}

const TIER_ORDER = { forged: 0, draft: 1, bare: 2 } as const;
const RECENT_LIMIT = 5;
const TAGS_COLLAPSED = 6;
const TAGS_EXPANDED = 12;

const rowId = (kind: string, id: string) => `ncp-${kind}-${id}`;

export const NewChatPickerPanel: React.FC<NewChatPickerPanelProps> = ({
  onClose, onSelect, initialSelectedTags, listboxId, titleId,
  showRecents = true, showFooterNav = true, maxHeight,
}) => {
  const { data: personas, isLoading, error, refetch } = usePersonas();
  const { data: sessions } = useChatSessions();
  const recent = useRecentChatPersonas(sessions, personas, RECENT_LIMIT);
  const allTopTags = useTopTags(personas || [], TAGS_EXPANDED);

  const [search, setSearch] = useState('');
  const [selectedTags, setSelectedTags] = useState<string[]>(initialSelectedTags ?? []);
  const [tagsExpanded, setTagsExpanded] = useState(false);

  const searchRef = useRef<HTMLInputElement>(null);
  useEffect(() => { searchRef.current?.focus(); }, []);

  const toggleTag = useCallback((tag: string) => {
    setSelectedTags(prev => prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag]);
  }, []);

  // Recent: respects tag chips, ignores search (intent-aligned). Data-gated off when
  // showRecents=false so flatNav/keyboard-index math stays honest (not just display-hidden).
  const filteredRecent = useMemo(() => {
    if (!showRecents) return [];
    if (selectedTags.length === 0) return recent;
    return recent.filter(r => {
      const t = new Set(r.persona.tags || []);
      return selectedTags.every(s => t.has(s));
    });
  }, [recent, selectedTags, showRecents]);

  // All: search via the shared substring hook, then tag-AND filter + tier-then-name sort.
  const searched = useFilteredList(personas ?? [], search,
    (p, q) => p.name.toLowerCase().includes(q)
      || (p.primarySpecialization || '').toLowerCase().includes(q)
      || (p.tags || []).some(t => t.toLowerCase().includes(q)));
  const filteredAll = useMemo(() => {
    const out = searched.filter(p => {
      if (selectedTags.length === 0) return true;
      const t = new Set(p.tags || []);
      return selectedTags.every(s => t.has(s));
    });
    return out.sort((a, b) => {
      const d = TIER_ORDER[getPersonaQualityTier(a)] - TIER_ORDER[getPersonaQualityTier(b)];
      return d !== 0 ? d : a.name.localeCompare(b.name);
    });
  }, [searched, selectedTags]);

  // Flattened keyboard nav order: Recent rows then All rows, each carrying its DOM row id so
  // aria-activedescendant lines up with the rendered row regardless of array identity.
  const navItems = useMemo(
    () => [
      ...filteredRecent.map(r => ({ persona: r.persona, id: rowId('recent', r.persona.id) })),
      ...filteredAll.map(p => ({ persona: p, id: rowId('all', p.id) })),
    ],
    [filteredRecent, filteredAll],
  );
  const { activeDescendantId, onKeyDown: listKeyDown } = useListboxKeyboard({
    items: navItems, isOpen: true, onCommit: it => onSelect(it.persona), getItemId: it => it.id,
  });

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      // Claim Esc (preventDefault) so a wrapping disclosure's document-Esc won't double-fire;
      // clear search first, else close (the panel owns close when rendered standalone).
      e.preventDefault();
      if (search) setSearch('');
      else onClose();
      return;
    }
    listKeyDown(e);
  };

  const visibleTags = tagsExpanded ? allTopTags : allTopTags.slice(0, TAGS_COLLAPSED);
  const hasResults = filteredRecent.length > 0 || filteredAll.length > 0;

  return (
    <div aria-labelledby={titleId} onKeyDown={onKeyDown}
      style={{ maxHeight: maxHeight ?? 600 }}
      className="flex flex-col w-[520px] max-w-[calc(100vw-32px)]">
      <h2 id={titleId} className="sr-only">Pick a Persona</h2>

      {/* Search */}
      <div className="relative p-3 border-b border-gray-100 dark:border-gray-700">
        <FiSearch className="absolute left-5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" aria-hidden="true" />
        <input
          ref={searchRef}
          role="combobox"
          aria-autocomplete="list"
          aria-expanded={true}
          aria-haspopup="listbox"
          aria-controls={listboxId}
          aria-activedescendant={activeDescendantId}
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search by name, tag, or specialization..."
          className="w-full pl-9 pr-3 py-2 rounded-lg text-sm bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-600 text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/40"
        />
      </div>

      {/* Tag chips */}
      {allTopTags.length > 0 && (
        <div className="px-3 py-2 border-b border-gray-100 dark:border-gray-700">
          <div className="flex items-center gap-1.5 flex-wrap" id="ncp-tag-chips">
            {visibleTags.map(tag => {
              const sel = selectedTags.includes(tag);
              return (
                <button key={tag} type="button" aria-pressed={sel} onClick={() => toggleTag(tag)}
                  className={`px-2.5 py-1 rounded-full text-xs min-h-[32px] transition-colors ${
                    sel ? 'bg-blue-600 text-white' : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                  }`}>
                  {tag}
                </button>
              );
            })}
            {allTopTags.length > TAGS_COLLAPSED && (
              <button type="button" aria-expanded={tagsExpanded} aria-controls="ncp-tag-chips"
                onClick={() => setTagsExpanded(v => !v)}
                className="px-2 py-1 min-h-[32px] text-xs text-gray-500 dark:text-gray-400 inline-flex items-center gap-0.5">
                {tagsExpanded ? 'less' : 'more'}
                <FiChevronDown className={`w-3 h-3 transition-transform ${tagsExpanded ? 'rotate-180' : ''}`} aria-hidden="true" />
              </button>
            )}
          </div>
        </div>
      )}

      {/* Lists */}
      <div role="listbox" id={listboxId} aria-labelledby={titleId} className="flex-1 overflow-y-auto p-1">
        {isLoading && (
          <div className="px-3 py-6 text-sm text-gray-400">Loading personas…</div>
        )}
        {error && (
          <div className="px-3 py-6 text-sm text-red-500">
            Failed to load personas.{' '}
            <button onClick={() => refetch()} className="underline">Retry</button>
          </div>
        )}
        {!isLoading && !error && personas && personas.length === 0 && (
          <div className="px-3 py-6 text-sm text-gray-500 dark:text-gray-400">
            {showFooterNav ? (
              <>
                No personas yet.{' '}
                <Link to="/personas/forge" className="text-emerald-600 dark:text-emerald-400 underline" onClick={onClose}>
                  Forge your first persona →
                </Link>
              </>
            ) : (
              <>No personas yet.</>
            )}
          </div>
        )}

        {!isLoading && !error && personas && personas.length > 0 && (
          <>
            {filteredRecent.length > 0 && (
              <div role="group" aria-labelledby="ncp-recent-heading">
                {/* role="presentation" so the label is the group's name (via aria-labelledby)
                    and NOT a stray non-option child of the listbox — matches the APG
                    grouped-listbox example. */}
                <h3 id="ncp-recent-heading" role="presentation" className="px-3 pt-2 pb-1 text-[11px] font-semibold uppercase tracking-wide text-gray-400">
                  Recently used ({filteredRecent.length})
                </h3>
                {filteredRecent.map((r) => (
                  <PersonaPickerRow key={`r-${r.persona.id}`} persona={r.persona} variant="compact"
                    lastActivity={r.lastActivity} id={rowId('recent', r.persona.id)}
                    isHighlighted={activeDescendantId === rowId('recent', r.persona.id)} onSelect={onSelect} />
                ))}
              </div>
            )}

            <div role="group" aria-labelledby="ncp-all-heading">
              <h3 id="ncp-all-heading" role="presentation" className="px-3 pt-2 pb-1 text-[11px] font-semibold uppercase tracking-wide text-gray-400">
                All personas ({filteredAll.length})
              </h3>
              {filteredAll.map((p) => (
                <PersonaPickerRow key={`a-${p.id}`} persona={p} variant="full"
                  id={rowId('all', p.id)}
                  isHighlighted={activeDescendantId === rowId('all', p.id)} onSelect={onSelect} />
              ))}
            </div>

            {!hasResults && (
              <div className="px-3 py-6 text-sm text-gray-500 dark:text-gray-400">
                No personas match.{' '}
                <button onClick={() => { setSearch(''); setSelectedTags([]); }} className="underline">Clear filters</button>
              </div>
            )}
          </>
        )}
      </div>

      {/* ONE computed SR status region — replaces the three competing aria-live
          regions that would stutter. */}
      <div role="status" aria-live="polite" aria-atomic="true" className="sr-only">
        {isLoading ? 'Loading personas'
          : error ? 'Failed to load personas'
          : !hasResults ? 'No personas match'
          : `${filteredAll.length} personas`}
      </div>

      {/* Footer */}
      {showFooterNav && personas && personas.length > 0 && (
        <div className="flex items-center justify-between px-3 py-2 border-t border-gray-100 dark:border-gray-700 text-xs">
          <Link to="/personas" onClick={onClose} className="text-gray-500 dark:text-gray-400 hover:text-emerald-600 dark:hover:text-emerald-400">
            Browse all personas →
          </Link>
          <Link to="/personas/forge" onClick={onClose} className="text-gray-500 dark:text-gray-400 hover:text-emerald-600 dark:hover:text-emerald-400">
            Forge a new persona →
          </Link>
        </div>
      )}
    </div>
  );
};
